/**
 * Tracking Lookup Form Component
 * Allows customers to look up booking status by plate + reference
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  isValidBookingReferenceFormat,
  normalizeBookingReference,
} from '@leal-crm/shared/utils/bookingReferenceUtils';

interface TrackingLookupFormProps {
  onLookup: (plate: string, reference: string) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  initialPlate?: string;
  initialReference?: string;
  language?: 'es' | 'en';
}

export function TrackingLookupForm({
  onLookup,
  isLoading = false,
  error,
  initialPlate = '',
  initialReference = '',
  language = 'es',
}: TrackingLookupFormProps) {
  const t = language === 'es' ? translations.es : translations.en;

  const [plate, setPlate] = useState(initialPlate);
  const [reference, setReference] = useState(initialReference);
  const [validationErrors, setValidationErrors] = useState<{
    plate?: string;
    reference?: string;
  }>({});

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-uppercase and remove invalid characters
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setPlate(value);
    if (validationErrors.plate) {
      setValidationErrors((prev) => ({ ...prev, plate: undefined }));
    }
  };

  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Auto-uppercase
    const value = e.target.value.toUpperCase();
    setReference(value);
    if (validationErrors.reference) {
      setValidationErrors((prev) => ({ ...prev, reference: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: { plate?: string; reference?: string } = {};

    if (!plate.trim()) {
      errors.plate = t.plateRequired;
    } else if (plate.length < 4) {
      errors.plate = t.plateTooShort;
    }

    if (!reference.trim()) {
      errors.reference = t.referenceRequired;
    } else if (!isValidBookingReferenceFormat(reference)) {
      errors.reference = t.referenceInvalid;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const normalizedRef = normalizeBookingReference(reference);
    if (normalizedRef) {
      await onLookup(plate, normalizedRef);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t.title}</h2>
        <p className="text-gray-500 mt-1">{t.subtitle}</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="plate">{t.plateLabel}</Label>
          <Input
            id="plate"
            type="text"
            value={plate}
            onChange={handlePlateChange}
            placeholder={t.platePlaceholder}
            className={`mt-1 font-mono uppercase ${
              validationErrors.plate ? 'border-red-500' : ''
            }`}
            maxLength={10}
            disabled={isLoading}
          />
          {validationErrors.plate && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.plate}</p>
          )}
        </div>

        <div>
          <Label htmlFor="reference">{t.referenceLabel}</Label>
          <Input
            id="reference"
            type="text"
            value={reference}
            onChange={handleReferenceChange}
            placeholder={t.referencePlaceholder}
            className={`mt-1 font-mono uppercase ${
              validationErrors.reference ? 'border-red-500' : ''
            }`}
            maxLength={12}
            disabled={isLoading}
          />
          {validationErrors.reference && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.reference}</p>
          )}
          <p className="text-gray-400 text-xs mt-1">{t.referenceHint}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {t.searching}
          </span>
        ) : (
          t.searchButton
        )}
      </Button>
    </form>
  );
}

const translations = {
  es: {
    title: 'Consultar estado de cita',
    subtitle: 'Introduce la matrícula y referencia de tu cita',
    plateLabel: 'Matrícula del vehículo',
    platePlaceholder: 'Ej: 1234ABC',
    plateRequired: 'La matrícula es obligatoria',
    plateTooShort: 'La matrícula debe tener al menos 4 caracteres',
    referenceLabel: 'Referencia de la cita',
    referencePlaceholder: 'Ej: LEAL-ABC123',
    referenceRequired: 'La referencia es obligatoria',
    referenceInvalid: 'Formato de referencia inválido (debe ser LEAL-XXXXXX)',
    referenceHint: 'La referencia se encuentra en el email de confirmación',
    searchButton: 'Buscar cita',
    searching: 'Buscando...',
  },
  en: {
    title: 'Check booking status',
    subtitle: 'Enter your vehicle plate and booking reference',
    plateLabel: 'Vehicle license plate',
    platePlaceholder: 'E.g.: 1234ABC',
    plateRequired: 'License plate is required',
    plateTooShort: 'License plate must be at least 4 characters',
    referenceLabel: 'Booking reference',
    referencePlaceholder: 'E.g.: LEAL-ABC123',
    referenceRequired: 'Booking reference is required',
    referenceInvalid: 'Invalid reference format (must be LEAL-XXXXXX)',
    referenceHint: 'You can find the reference in your confirmation email',
    searchButton: 'Search booking',
    searching: 'Searching...',
  },
};

export default TrackingLookupForm;
